import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { themeCssVar as theme } from '@tinderapp/ui-react';
import classNames from 'classnames';

import { getRoute } from 'Config';
import { connectConfigService } from 'Lib/ConfigService';
import {
  LEVER_GOLD_NEW_LIKES_INSENDIO_MODAL_ENABLED,
  LEVER_GOLD_NEW_LIKES_MODAL_FREQUENCY,
} from 'Lib/ConfigService/levers';
import { ONE_DAY } from 'Lib/constants/time';
import {
  UI_THEME_GOLD,
  UI_THEME_PLATINUM,
  UI_THEME_REGULAR,
  UI_THEME_UNKNOWN,
} from 'Lib/UiThemeContext';
import isExpired from 'Lib/utils/isExpired';
import useNavigate from 'Hooks/useNavigate';
import useTimeout from 'Hooks/useTimeout';

import { getIsPurchaseReducerRehydrated, getUiTheme } from 'Store/app/selectors';
import { getIsPremiumEnabled } from 'Store/appSettings/selectors';
import { getLikesYouCount, getLikesYouIsRange, getNewLikesCount } from 'Store/likesYou/selectors';
import {
  getHasPlatinumSubscription,
  getHasPremiumSubscription,
} from 'Store/profile/revenue.selectors';
import { setUpsellShown } from 'Store/purchase/actions';
import { getGoldHomeTooltipShownTimeStamp } from 'Store/purchase/selectors';
import { getCurrentPath } from 'Store/routing/selectors';

import { trackEvent } from 'Store/sparks/actions';
import { APP_POPUP } from 'Store/sparks/insendio/events';

import ToolTip from 'Components/ToolTip';

import Star from 'Assets/reactSvgs/Star';

import NavBarLinkBase from './NavBarLinkBase';

const activePath = new Set([
  getRoute('goldHome', { pathConfig: { withPrefix: false } }),
  getRoute('myLikes', { pathConfig: { withPrefix: false } }),
]);

type Props = {
  newLikesInsendioModalEnabled: boolean;
  newLikesModalFrequency: number;
};

const commonAppPopupEventData = {
  location: 'discovery',
  name: 'likes count',
  type: 'tooltip',
};

const TOOLTIP_TIMEOUT = 5000;

const GoldHomeNavBarLink = (props: Props) => {
  const { newLikesInsendioModalEnabled, newLikesModalFrequency } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentPath = useSelector(getCurrentPath);
  const hasPlatinum = useSelector(getHasPlatinumSubscription);
  const hasPremiumSubscription = useSelector(getHasPremiumSubscription);
  const isPremiumEnabled = useSelector(getIsPremiumEnabled);
  const isRange = useSelector(getLikesYouIsRange);
  const likesYouCount = useSelector(getLikesYouCount);
  const uiTheme = useSelector(getUiTheme);
  const newLikesCount = useSelector(getNewLikesCount);
  const tooltipTimeStamp = useSelector(getGoldHomeTooltipShownTimeStamp);
  const isPersistRehydrated = useSelector(getIsPurchaseReducerRehydrated);

  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
  const isLinkActive = activePath.has(currentPath);
  const { formatMessage } = useIntl();

  const onRecsPage = currentPath === getRoute('recs', { pathConfig: { withPrefix: false } });

  const setTooltipShown = useCallback(() => {
    if (shouldShowTooltip) {
      // stop showing tooltip
      setShouldShowTooltip(false);
      const currentTime = Date.now();
      // set new timestamp in redux
      dispatch(setUpsellShown({ upsellType: 'goldHomeTooltip', upsellTs: currentTime }));
    }
  }, [dispatch, shouldShowTooltip]);

  const tooltipTimer = useTimeout(() => {
    setTooltipShown();
  }, TOOLTIP_TIMEOUT);

  const handleTooltipShown = useCallback(() => {
    tooltipTimer.start();

    dispatch(
      trackEvent(APP_POPUP, {
        ...commonAppPopupEventData,
        action: 'show',
      })
    );
  }, [dispatch, tooltipTimer]);

  useEffect(() => {
    const showTooltipInsteadOfCelebrationModal = newLikesModalFrequency === 0;
    const showInsendioModalInsteadOfTooltip =
      newLikesInsendioModalEnabled && !hasPremiumSubscription && isRange;
    const canShowTooltip =
      !showInsendioModalInsteadOfTooltip && showTooltipInsteadOfCelebrationModal;
    // Need to check whether persist value has loaded before checking timestamp expiration
    if (canShowTooltip && isPersistRehydrated && isExpired(tooltipTimeStamp, ONE_DAY)) {
      // Show tooltip if it's been over a day since it was last viewed
      setShouldShowTooltip(true);
    }
  }, [
    tooltipTimer,
    dispatch,
    hasPremiumSubscription,
    isPersistRehydrated,
    isRange,
    newLikesInsendioModalEnabled,
    newLikesModalFrequency,
    tooltipTimeStamp,
  ]);

  const count = useMemo(
    () =>
      likesYouCount ? (
        <span className="Typs(caption-1-regular) C($c-ds-text-primary-inverse) Miw(12px) Ta(c)">
          {Math.min(99, likesYouCount)}
          {isRange || likesYouCount > 99 ? '+' : ''}
        </span>
      ) : null,
    [isRange, likesYouCount]
  );

  const navigationPayload = useMemo(
    () => ({
      sessionDestination: 'goldHomepage',
      sessionTogglesAvailable: 9,
      sessionTooltipShown: shouldShowTooltip,
    }),
    [shouldShowTooltip]
  );

  const sendGoldHomeAnalytics = useCallback(() => {
    dispatch(
      trackEvent(APP_POPUP, {
        ...commonAppPopupEventData,
        action: 'tap',
      })
    );
  }, [dispatch]);

  const navigateToGoldHome = useCallback(() => {
    sendGoldHomeAnalytics();
    navigate(getRoute('goldHome'));
    setTooltipShown();
    tooltipTimer.clear();
  }, [navigate, sendGoldHomeAnalytics, setTooltipShown, tooltipTimer]);

  const fillColor = useMemo(() => {
    if (!isLinkActive) {
      return '';
    }

    switch (uiTheme) {
      case UI_THEME_PLATINUM:
        return theme.gradientFill.backgroundPlatinum;
      case UI_THEME_GOLD:
        return theme.gradientFill.backgroundGold;
      case UI_THEME_UNKNOWN:
        return theme.color.gray15;
      case UI_THEME_REGULAR:
      default:
        return theme.gradientFill.backgroundBrandGradient;
    }
  }, [isLinkActive, uiTheme]);

  const backgroundClassName = useMemo(
    () =>
      classNames({
        'Bgi($g-ds-background-brand-gradient)': uiTheme === UI_THEME_REGULAR,
        'Bgi($g-ds-background-gold)': uiTheme === UI_THEME_GOLD,
        'Bgi($g-ds-background-platinum)': uiTheme === UI_THEME_PLATINUM,
      }),
    [uiTheme]
  );

  const tooltipHeader = useMemo(() => {
    if ((hasPremiumSubscription || !isRange) && newLikesCount > 0) {
      return 'ICU.newLikeCount';
    }
    return likesYouCount === 1
      ? 'TinderGold.goldHomeToolTipOneLike'
      : 'TinderGold.goldHomeToolTipMultipleLikes';
  }, [hasPremiumSubscription, isRange, likesYouCount, newLikesCount]);

  const goldHomeContent = useMemo(
    () => (
      <>
        {count && (
          <span
            className={classNames(
              'Pos(a) End(-20%) T(10%) CenterAlign',
              'Bdrs(16px) Px(4px) Py(2px) Scale(.9)',
              backgroundClassName
            )}
          >
            {count}
          </span>
        )}
        <Star
          className={classNames('Sq(32px) C($c-ds-icon-navigation-primary-inactive) Trsdu($fast)')}
          fill={fillColor}
        />
      </>
    ),
    [count, backgroundClassName, fillColor]
  );

  if (!isPremiumEnabled) {
    return null;
  }

  return (
    <>
      <NavBarLinkBase
        className="Pos(r)"
        navigationPayload={navigationPayload}
        i13nModel={{ message: 'Gold Home' }}
        intlMessage={formatMessage({ id: 'TinderGold.likesYou' })}
        to={getRoute('goldHome')}
        onClick={sendGoldHomeAnalytics}
        data-testid="goldHomeLink"
      >
        {goldHomeContent}
        {shouldShowTooltip && onRecsPage && !!likesYouCount && (
          <ToolTip
            caratPosition="bottom"
            theme={hasPlatinum ? 'platinum' : 'gold'}
            header={tooltipHeader}
            headerValues={{ number: newLikesCount }}
            className="Pos(a) B(0) Z(3) Mb(4px) TranslateY(-50%) Mx(-100%)"
            onClick={navigateToGoldHome}
            onTooltipShown={handleTooltipShown}
          />
        )}
      </NavBarLinkBase>
    </>
  );
};

export default connectConfigService({
  [LEVER_GOLD_NEW_LIKES_MODAL_FREQUENCY]: 'newLikesModalFrequency',
  [LEVER_GOLD_NEW_LIKES_INSENDIO_MODAL_ENABLED]: 'newLikesInsendioModalEnabled',
})(memo(GoldHomeNavBarLink));
